import type { AdminPickerDetailDto } from '@carcheck/shared';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Drawer from '@mui/material/Drawer';
import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { PickerDetailFooter } from './_components/PickerDetailFooter';
import { PickerDetailHeader } from './_components/PickerDetailHeader';
import { PickerProfileSection } from './_components/PickerProfileSection';
import { PickerReasonDialog } from './_components/PickerReasonDialog';
import { PickerRecentReports } from './_components/PickerRecentReports';
import { usePickerAccessActions } from './_lib/usePickerAccessActions';
import { useAdminPickerDetail, type PickerAccessAction } from '../../api/admin-pickers';

/**
 * Picker detail drawer (AUT-400), after `pickers-detail.html`: header,
 * «отключён» callout, «Профиль», «Последние отчёты», and a footer with
 * disable / enable — each behind a reason dialog.
 *
 * One scrolling body, no tabs: the mockup's «Журнал» tab is out of scope,
 * and «Отчёты» is the «Последние отчёты» block. The company (name + role)
 * sits in «Профиль». The detail query runs only while `pickerId` is set.
 */
const DRAWER_WIDTH = 720;

const DrawerBody = ({
  picker,
  onClose,
}: {
  picker: AdminPickerDetailDto;
  onClose: () => void;
}): ReactElement => {
  const { t } = useTranslation();
  const [dialog, setDialog] = useState<PickerAccessAction | null>(null);
  const actions = usePickerAccessActions();

  const confirm = (reason: string): void => {
    if (dialog === null) return;
    void actions.run(picker.userId, dialog, reason).then((ok) => {
      if (ok) setDialog(null);
    });
  };

  const subject = [picker.name, picker.phone].filter(Boolean).join(' · ');

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          width: { xs: '100vw', sm: DRAWER_WIDTH },
          maxWidth: '100vw',
        }}
      >
        <PickerDetailHeader picker={picker} onClose={onClose} />
        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            p: '22px 24px',
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
          }}
        >
          <PickerProfileSection picker={picker} />
          <PickerRecentReports reports={picker.recentReports} reportCount={picker.reportCount} />
        </Box>
        <PickerDetailFooter
          disabled={picker.disabled !== null}
          busy={actions.busy}
          onDisable={() => setDialog('disable')}
          onEnable={() => setDialog('enable')}
        />
      </Box>
      <PickerReasonDialog
        open={dialog !== null}
        action={dialog ?? 'disable'}
        subject={subject || t('pickers.noValue')}
        activeLinkCount={picker.activeLinkCount}
        loading={actions.busy}
        onConfirm={confirm}
        onCancel={() => setDialog(null)}
      />
    </>
  );
};

export const PickerDetailDrawer = ({
  pickerId,
  onClose,
}: {
  pickerId: string | null;
  onClose: () => void;
}): ReactElement => {
  const { t } = useTranslation();
  const query = useAdminPickerDetail(pickerId);
  const width = { xs: '100vw', sm: DRAWER_WIDTH };

  return (
    <Drawer anchor="right" open={pickerId !== null} onClose={onClose}>
      {query.isLoading ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width,
            height: '100%',
          }}
        >
          <CircularProgress />
        </Box>
      ) : null}
      {query.error && !query.data ? (
        <Box sx={{ width, p: 3 }}>
          <Alert severity="error">{t('pickers.detail.loadError')}</Alert>
        </Box>
      ) : null}
      {query.data ? (
        <DrawerBody key={query.data.userId} picker={query.data} onClose={onClose} />
      ) : null}
    </Drawer>
  );
};
